import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiTrash2, FiAlertCircle, FiCheckCircle, FiLayers } from "react-icons/fi";

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (inputValue?: string) => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  type?: "info" | "danger" | "warning" | "success";
  showInput?: boolean;
  inputPlaceholder?: string;
  inputValueRequired?: boolean;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  type = "info",
  showInput = false,
  inputPlaceholder = "Provide details...",
  inputValueRequired = false
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [textVal, setTextVal] = useState("");

  useEffect(() => {
    if (isOpen) {
      setTextVal("");
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "auto";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getTypeStyles = () => {
    switch (type) {
      case "danger":
        return {
          icon: <FiTrash2 className="w-6 h-6 text-red-600" />,
          bg: "bg-red-50",
          button: "bg-red-600 hover:bg-red-700 text-white focus:ring-red-500",
        };
      case "warning":
        return {
          icon: <FiAlertCircle className="w-6 h-6 text-amber-600" />,
          bg: "bg-amber-50",
          button: "bg-amber-500 hover:bg-amber-600 text-white focus:ring-amber-500",
        };
      case "success":
        return {
          icon: <FiCheckCircle className="w-6 h-6 text-emerald-600" />,
          bg: "bg-emerald-50",
          button: "bg-emerald-600 hover:bg-emerald-700 text-white focus:ring-emerald-500",
        };
      default:
        return {
          icon: <FiLayers className="w-6 h-6 text-blue-600" />,
          bg: "bg-blue-50",
          button: "bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500",
        };
    }
  };

  const styles = getTypeStyles();
  const isConfirmDisabled = showInput && inputValueRequired && !textVal.trim();

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div
        ref={modalRef}
        className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-100 overflow-hidden transform transition-all scale-100 animate-in fade-in zoom-in-95 duration-200"
      >
        <div className="p-6 text-left">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-xl ${styles.bg} flex-shrink-0`}>
              {styles.icon}
            </div>
            <div className="flex-1 text-left">
              <h3 className="text-base font-bold text-slate-900 leading-6">
                {title}
              </h3>
              <p className="mt-2 text-xs font-medium text-slate-500 leading-relaxed">
                {message}
              </p>
              {showInput && (
                <div className="mt-4">
                  <textarea
                    rows={3}
                    value={textVal}
                    onChange={(e) => setTextVal(e.target.value)}
                    placeholder={inputPlaceholder}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs font-medium focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-0 resize-none transition-all"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="bg-slate-50 px-6 py-4 flex items-center justify-end gap-3 border-t border-slate-100">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-slate-200 text-slate-700 rounded-xl text-xs font-bold uppercase tracking-wider hover:bg-slate-100 transition-all"
          >
            {cancelLabel}
          </button>
          <button
            disabled={isConfirmDisabled}
            onClick={() => {
              onConfirm(textVal);
              onClose();
            }}
            className={`px-5 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed ${styles.button}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
